import React, { useState, useEffect, useCallback } from "react";
import {
  getBillingCycles,
  getBillingCycleDetail,
  createBillingCycle,
  updateBillingCycle,
  payBillingCycle,
  deleteBillingCycle,
  getCompletedRoutesForBilling,
  getEmployees,
  createGlobalBillingCycles,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  Plus, X, ChevronDown, ChevronUp, Check, Trash2, Edit2,
  DollarSign, Calendar, User, FileText, Copy, CheckCircle,
  RotateCcw, Loader, ExternalLink, Image, Globe,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(d) {
  if (!d) return "—";
  return format(new Date(d), "dd/MM/yyyy", { locale: es });
}

function fmtMoney(n) {
  if (!n) return "$ 0";
  return `$ ${Number(n).toLocaleString("es-AR")}`;
}

const EMPTY_FIELD = { field_name: "", field_value: "" };

// ── Create/Edit modal ─────────────────────────────────────────────────────────

function CycleModal({ mode, cycle, employees, onClose, onSaved }) {
  const [userId, setUserId]       = useState(cycle?.user_id || "");
  const [start, setStart]         = useState(cycle?.period_start?.slice(0, 10) || "");
  const [end, setEnd]             = useState(cycle?.period_end?.slice(0, 10) || "");
  const [amount, setAmount]       = useState(cycle ? String(cycle.amount) : "");
  const [notes, setNotes]         = useState(cycle?.notes || "");
  const [fields, setFields]       = useState([]);
  const [routes, setRoutes]       = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState(new Set());
  const [loadingRoutes, setLoadingRoutes]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  // Load fields and routes when editing
  useEffect(() => {
    if (mode === "edit" && cycle) {
      getBillingCycleDetail(cycle.id).then(detail => {
        setFields(detail.fields.length > 0 ? detail.fields : [{ ...EMPTY_FIELD }]);
        setSelectedRoutes(new Set(detail.routes.map(r => r.assignment_id)));
      }).catch(() => {});
    } else {
      setFields([
        { field_name: 'Razón Social', field_value: 'NSR S.A' },
        { field_name: 'CUIT',        field_value: '30-71100698-9' },
        { field_name: 'IVA',         field_value: 'Responsable Inscripto' },
      ]);
    }
  }, [mode, cycle]);

  // Load routes when user is selected
  useEffect(() => {
    if (!userId) { setRoutes([]); return; }
    setLoadingRoutes(true);
    getCompletedRoutesForBilling(userId)
      .then(setRoutes)
      .catch(() => setRoutes([]))
      .finally(() => setLoadingRoutes(false));
  }, [userId]);

  function toggleRoute(id) {
    setSelectedRoutes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function addField() { setFields(f => [...f, { ...EMPTY_FIELD }]); }
  function removeField(i) { setFields(f => f.filter((_, idx) => idx !== i)); }
  function updateField(i, key, val) {
    setFields(f => f.map((item, idx) => idx === i ? { ...item, [key]: val } : item));
  }

  async function handleSave() {
    if (!userId) return setError("Seleccioná un usuario");
    if (!start || !end) return setError("Ingresá el período");
    if (!amount || isNaN(Number(amount))) return setError("Ingresá un importe válido");
    setError(""); setSaving(true);
    try {
      const payload = {
        user_id: userId,
        period_start: start,
        period_end: end,
        amount: Number(amount),
        notes: notes.trim() || null,
        fields: fields.filter(f => f.field_name.trim()),
        assignment_ids: [...selectedRoutes],
      };
      if (mode === "create") {
        await createBillingCycle(payload);
      } else {
        await updateBillingCycle(cycle.id, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={m.sheet}>
        {/* Header */}
        <div style={m.header}>
          <span style={m.headerTitle}>
            {mode === "create" ? "Nuevo ciclo de facturación" : "Editar ciclo"}
          </span>
          <button style={m.closeBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={m.body}>
          {/* User */}
          <label style={m.label}>Usuario</label>
          <select
            style={m.select}
            value={userId}
            onChange={e => setUserId(e.target.value)}
            disabled={mode === "edit"}
          >
            <option value="">— Seleccionar —</option>
            {employees.map(u => (
              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
            ))}
          </select>

          {/* Period */}
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={m.label}>Inicio del período</label>
              <input type="date" style={m.input} value={start} onChange={e => setStart(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={m.label}>Fin del período</label>
              <input type="date" style={m.input} value={end} onChange={e => setEnd(e.target.value)} />
            </div>
          </div>

          {/* Amount */}
          <label style={m.label}>Importe</label>
          <input
            type="number"
            style={m.input}
            placeholder="0.00"
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />

          {/* Notes */}
          <label style={m.label}>Notas (opcional)</label>
          <textarea
            style={{ ...m.input, height: 64, resize: "vertical" }}
            placeholder="Observaciones internas..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />

          {/* Recipient fields */}
          <div style={m.sectionHeader}>
            <span style={m.sectionLabel}>Datos del destinatario</span>
            <button style={m.addFieldBtn} onClick={addField}>
              <Plus size={13} /> Agregar campo
            </button>
          </div>
          {fields.map((f, i) => (
            <div key={i} style={m.fieldRow}>
              <input
                style={{ ...m.input, flex: "0 0 130px", marginBottom: 0 }}
                placeholder="Nombre (ej. CBU)"
                value={f.field_name}
                onChange={e => updateField(i, "field_name", e.target.value)}
              />
              <input
                style={{ ...m.input, flex: 1, marginBottom: 0 }}
                placeholder="Valor"
                value={f.field_value}
                onChange={e => updateField(i, "field_value", e.target.value)}
              />
              <button style={m.removeFieldBtn} onClick={() => removeField(i)}>
                <X size={14} />
              </button>
            </div>
          ))}

          {/* Routes */}
          <div style={{ ...m.sectionHeader, marginTop: 16 }}>
            <span style={m.sectionLabel}>Rutas incluidas</span>
            {selectedRoutes.size > 0 && (
              <span style={m.routeCountBadge}>{selectedRoutes.size} selec.</span>
            )}
          </div>
          {!userId ? (
            <p style={m.routeEmpty}>Seleccioná un usuario para ver sus rutas</p>
          ) : loadingRoutes ? (
            <div style={m.routeEmpty}><Loader size={16} /> Cargando...</div>
          ) : routes.length === 0 ? (
            <p style={m.routeEmpty}>No hay rutas completadas para este usuario</p>
          ) : (
            <div style={m.routeList}>
              {routes.map(r => (
                <label key={r.id} style={{ ...m.routeRow, ...(r.in_cycle && !selectedRoutes.has(r.id) ? m.routeRowDim : {}) }}>
                  <input
                    type="checkbox"
                    checked={selectedRoutes.has(r.id)}
                    onChange={() => toggleRoute(r.id)}
                    style={{ flexShrink: 0 }}
                  />
                  <div style={m.routeInfo}>
                    <span style={m.routeName}>{r.route_name}</span>
                    <span style={m.routeMeta}>
                      {fmt(r.completed_at)} · {r.checked_meters}/{r.total_meters} medidores
                      {r.price ? ` · $ ${Number(r.price).toLocaleString("es-AR")}` : ""}
                      {r.in_cycle && <span style={m.inCycleTag}> · ya en ciclo</span>}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          )}

          {error && <p style={m.errorText}>{error}</p>}
        </div>

        {/* Footer */}
        <div style={m.footer}>
          <button style={m.cancelBtn} onClick={onClose} disabled={saving}>Cancelar</button>
          <button style={m.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? <Loader size={15} style={{ animation: "spin 1s linear infinite" }} /> : <Check size={15} />}
            {mode === "create" ? "Crear ciclo" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Cycle detail panel ────────────────────────────────────────────────────────

function openFactura(f) {
  if (f.file_type !== "pdf") {
    window.open(f.url, "_blank");
    return;
  }
  // PDFs from Cloudinary raw uploads lack Content-Type; fetch and re-wrap as PDF blob
  const newTab = window.open("", "_blank"); // no noopener — we need to control this tab
  fetch(f.url)
    .then(r => r.arrayBuffer())
    .then(buf => {
      const blob = new Blob([buf], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      if (newTab && !newTab.closed) {
        newTab.location.href = blobUrl;
      } else {
        window.open(blobUrl, "_blank");
      }
    })
    .catch(() => {
      if (newTab && !newTab.closed) newTab.location.href = f.url;
    });
}

function CycleDetail({ cycle, onEdit, onPay, onDelete, isAdminPlus }) {
  const [detail, setDetail] = useState(null);
  const [open, setOpen]     = useState(false);
  const [copied, setCopied] = useState(null);

  function loadDetail() {
    if (!detail) {
      getBillingCycleDetail(cycle.id).then(setDetail).catch(() => {});
    }
    setOpen(v => !v);
  }

  function copyField(val, i) {
    navigator.clipboard?.writeText(val).then(() => {
      setCopied(i);
      setTimeout(() => setCopied(null), 1800);
    });
  }

  const isPaid = cycle.status === "paid";

  return (
    <div style={d.card}>
      {/* Card header */}
      <div style={d.cardHead}>
        <div style={d.cycleIcon}>
          <FileText size={16} color="#D4620D" />
        </div>
        <div style={d.cycleInfo}>
          <span style={d.cycleName}>{cycle.user_name}</span>
          <span style={d.cyclePeriod}>
            {fmt(cycle.period_start)} — {fmt(cycle.period_end)}
          </span>
        </div>
        <div style={d.cycleRight}>
          <span style={{ ...d.amountText, color: isPaid ? "#16a34a" : "#D4620D" }}>
            {fmtMoney(cycle.amount)}
          </span>
          <span style={{ ...d.statusBadge, ...(isPaid ? d.statusPaid : d.statusPending) }}>
            {isPaid ? "Pagado" : "Pendiente"}
          </span>
        </div>
        <button style={d.expandBtn} onClick={loadDetail}>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Expanded detail */}
      {open && (
        <div style={d.cardBody}>
          {/* Meta */}
          <div style={d.metaRow}>
            <span style={d.metaLabel}>Rutas</span>
            <span style={d.metaValue}>{cycle.route_count}</span>
            <span style={d.metaDot} />
            <span style={d.metaLabel}>Facturas</span>
            <span style={d.metaValue}>{cycle.factura_count}</span>
            <span style={d.metaDot} />
            <span style={d.metaLabel}>Creado</span>
            <span style={d.metaValue}>{fmt(cycle.created_at)}</span>
            {isPaid && (
              <>
                <span style={d.metaDot} />
                <span style={d.metaLabel}>Pagado</span>
                <span style={d.metaValue}>{fmt(cycle.paid_at)}</span>
              </>
            )}
          </div>

          {cycle.notes && (
            <p style={d.notes}>{cycle.notes}</p>
          )}

          {/* Recipient fields */}
          {detail?.fields?.length > 0 && (
            <div style={d.fieldsSection}>
              <p style={d.fieldsTitle}>Datos del destinatario</p>
              {detail.fields.map((f, i) => (
                <div key={f.id} style={d.fieldRow}>
                  <span style={d.fieldName}>{f.field_name}</span>
                  <span style={d.fieldValue}>{f.field_value || "—"}</span>
                  <button style={d.copyBtn} onClick={() => copyField(f.field_value, i)} title="Copiar">
                    {copied === i ? <CheckCircle size={13} color="#16a34a" /> : <Copy size={13} />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Routes */}
          {detail?.routes?.length > 0 && (
            <div style={d.routesSection}>
              <p style={d.fieldsTitle}>Rutas ({detail.routes.length})</p>
              {detail.routes.map((r, i) => {
                const premios = Array.isArray(r.premios_alcanzados) ? r.premios_alcanzados : [];
                const valorBase = Number(r.valor) || 0;
                const valorPremios = premios.reduce((s, p) => s + (Number(p.monto) || 0), 0);
                const total = valorBase + valorPremios;
                return (
                  <div key={i} style={d.routeItem}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <span style={d.routeItemName}>{r.route_name}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "#D4620D", whiteSpace: "nowrap" }}>
                        {fmtMoney(total)}
                      </span>
                    </div>
                    <span style={d.routeItemMeta}>{fmt(r.completed_at)}</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
                      <span style={d.routeChip}>
                        Ruta: {fmtMoney(valorBase)}
                      </span>
                      {premios.map((p, pi) => (
                        <span key={pi} style={d.premioChip}>
                          Premio "{p.nombre}": {fmtMoney(p.monto)}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Facturas */}
          {detail && (
            <div style={d.facturasSection}>
              <p style={d.fieldsTitle}>
                Facturas del usuario ({detail.facturas?.length || 0})
              </p>
              {!detail.facturas?.length ? (
                <p style={d.facturasEmpty}>El usuario aún no subió facturas para este ciclo</p>
              ) : (
                detail.facturas.map((f) => (
                  <div key={f.id} style={d.facturaRow}>
                    <div style={d.facturaIcon}>
                      {f.file_type === "image"
                        ? <Image size={14} color="#D4620D" />
                        : <FileText size={14} color="#D4620D" />}
                    </div>
                    <div style={d.facturaInfo}>
                      <span style={d.facturaName}>{f.nombre || (f.file_type === "pdf" ? "factura.pdf" : "factura.jpg")}</span>
                      <span style={d.facturaMeta}>{f.file_type.toUpperCase()} · {fmt(f.created_at)}</span>
                    </div>
                    {f.file_type === "image" ? (
                      <a href={f.url} target="_blank" rel="noreferrer" style={d.facturaThumbLink}>
                        <img src={f.url} alt={f.nombre} style={d.facturaThumb} />
                      </a>
                    ) : null}
                    <button style={d.facturaOpenBtn} onClick={() => openFactura(f)}>
                      <ExternalLink size={13} /> Abrir
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Aclaraciones */}
          <div style={d.aclaracionesBox}>
            <p style={{ ...d.fieldsTitle, marginBottom: 10 }}>Aclaraciones para el operario</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <p style={d.aclaracionItem}>• Realizar la factura como <strong>"Servicio"</strong></p>
              <p style={d.aclaracionItem}>• Período: <strong>{fmt(cycle.period_start)} - {fmt(cycle.period_end)}</strong></p>
              <p style={d.aclaracionItem}>• Artículo o Ítem: <strong>"Servicio de Auditoría"</strong></p>
            </div>
          </div>

          {/* Actions */}
          {isAdminPlus && (
            <div style={d.actions}>
              {!isPaid && (
                <button style={d.payBtn} onClick={() => onPay(cycle)}>
                  <CheckCircle size={14} /> Marcar como pagado
                </button>
              )}
              <button style={d.editBtn} onClick={() => onEdit(cycle)}>
                <Edit2 size={14} /> Editar
              </button>
              <button style={d.deleteBtn} onClick={() => onDelete(cycle)}>
                <Trash2 size={14} /> Eliminar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Global cycle modal ────────────────────────────────────────────────────────

function GlobalCycleModal({ onClose, onDone }) {
  const [start, setStart]   = useState("");
  const [end, setEnd]       = useState("");
  const [notes, setNotes]   = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [result, setResult] = useState(null); // { created, cycles }

  async function handleGenerate() {
    if (!start || !end) return setError("Ingresá el rango de fechas");
    if (end < start)    return setError("La fecha de fin debe ser mayor o igual al inicio");
    setError(""); setSaving(true);
    try {
      const res = await createGlobalBillingCycles({ period_start: start, period_end: end, notes: notes.trim() || null });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const totalAmount = result?.cycles?.reduce((s, c) => s + Number(c.amount), 0) ?? 0;

  return (
    <div style={m.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...m.sheet, maxWidth: 480 }}>
        {/* Header */}
        <div style={m.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Globe size={15} color="#D4620D" />
            </div>
            <span style={m.headerTitle}>Generar ciclos globales</span>
          </div>
          <button style={m.closeBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={m.body}>
          {!result ? (
            <>
              <p style={{ fontSize: 13, color: "var(--text-2)", margin: 0, lineHeight: 1.6 }}>
                Se generará un ciclo de facturación por cada usuario que tenga rutas completadas (sin pagar y sin ciclo asignado) dentro del rango de fechas seleccionado. El importe se calcula automáticamente sumando el valor de cada ruta.
              </p>

              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={m.label}>Fecha de inicio *</label>
                  <input type="date" style={m.input} value={start} onChange={e => setStart(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={m.label}>Fecha de fin *</label>
                  <input type="date" style={m.input} value={end} onChange={e => setEnd(e.target.value)} />
                </div>
              </div>

              <div>
                <label style={m.label}>Notas (opcional)</label>
                <textarea
                  style={{ ...m.input, height: 60, resize: "vertical" }}
                  placeholder="Ej: Quincena del 1 al 15 de mayo..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </div>

              {error && <p style={{ fontSize: 13, color: "#ef4444", fontWeight: 600, margin: 0 }}>{error}</p>}
            </>
          ) : (
            <div>
              {result.created === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                  <p style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", margin: "0 0 8px" }}>Sin rutas para facturar</p>
                  <p style={{ fontSize: 13, color: "var(--text-2)", margin: 0 }}>
                    No se encontraron rutas completadas sin pagar en ese período.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
                    <p style={{ fontSize: 15, fontWeight: 800, color: "#166534", margin: "0 0 4px" }}>
                      ✓ {result.created} ciclo{result.created !== 1 ? "s" : ""} generado{result.created !== 1 ? "s" : ""}
                    </p>
                    <p style={{ fontSize: 13, color: "#166534", margin: 0 }}>
                      Total acumulado: <strong>{fmtMoney(totalAmount)}</strong>
                    </p>
                  </div>
                  <div style={{ maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                    {result.cycles.map((c, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--bg-subtle)", borderRadius: 10, padding: "10px 14px" }}>
                        <div>
                          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Usuario #{c.user_id.slice(-6)}</p>
                          <p style={{ margin: 0, fontSize: 12, color: "var(--text-2)" }}>{c.route_count} ruta{c.route_count !== 1 ? "s" : ""}</p>
                        </div>
                        <span style={{ fontSize: 14, fontWeight: 800, color: "#D4620D" }}>{fmtMoney(c.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div style={m.footer}>
          {!result ? (
            <>
              <button style={m.cancelBtn} onClick={onClose} disabled={saving}>Cancelar</button>
              <button style={m.saveBtn} onClick={handleGenerate} disabled={saving || !start || !end}>
                {saving
                  ? <><Loader size={15} style={{ animation: "spin 1s linear infinite" }} /> Generando...</>
                  : <><Globe size={15} /> Generar ciclos</>}
              </button>
            </>
          ) : (
            <button style={{ ...m.saveBtn, flex: 1 }} onClick={() => { onDone(); onClose(); }}>
              <Check size={15} /> Listo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function FacturacionPage() {
  const { user } = useAuth();
  const isAdminPlus = user?.role === "admin-plus";

  const [cycles, setCycles]       = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");

  // Filters
  const [filterUser, setFilterUser]     = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Modal state
  const [modal, setModal]           = useState(null); // null | { mode: "create"|"edit", cycle? }
  const [confirm, setConfirm]       = useState(null); // null | { type: "pay"|"delete", cycle }
  const [globalModal, setGlobalModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const opts = {};
      if (filterUser)   opts.user_id = filterUser;
      if (filterStatus) opts.status  = filterStatus;
      setCycles(await getBillingCycles(opts));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getEmployees().then(setEmployees).catch(() => {}); }, []);

  async function handlePay(cycle) {
    setActionLoading(true);
    try {
      await payBillingCycle(cycle.id);
      setCycles(prev => prev.map(c => c.id === cycle.id ? { ...c, status: "paid", paid_at: new Date().toISOString() } : c));
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  }

  async function handleDelete(cycle) {
    setActionLoading(true);
    try {
      await deleteBillingCycle(cycle.id);
      setCycles(prev => prev.filter(c => c.id !== cycle.id));
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  }

  const totalAmount = cycles.reduce((s, c) => s + Number(c.amount), 0);
  const paidCount   = cycles.filter(c => c.status === "paid").length;

  return (
    <div style={p.root}>
      {/* Page header */}
      <div style={p.pageHeader}>
        <div>
          <h1 style={p.title}>Facturación</h1>
          <p style={p.subtitle}>Ciclos de facturación por usuario</p>
        </div>
        {isAdminPlus && (
          <div style={{ display: "flex", gap: 10 }}>
            <button style={p.globalBtn} onClick={() => setGlobalModal(true)}>
              <Globe size={16} /> Generar global
            </button>
            <button style={p.newBtn} onClick={() => setModal({ mode: "create" })}>
              <Plus size={16} /> Nuevo ciclo
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={p.statsRow}>
        <div style={p.statCard}>
          <span style={p.statValue}>{cycles.length}</span>
          <span style={p.statLabel}>Ciclos totales</span>
        </div>
        <div style={p.statCard}>
          <span style={{ ...p.statValue, color: "#16a34a" }}>{paidCount}</span>
          <span style={p.statLabel}>Pagados</span>
        </div>
        <div style={p.statCard}>
          <span style={{ ...p.statValue, color: "#f59e0b" }}>{cycles.length - paidCount}</span>
          <span style={p.statLabel}>Pendientes</span>
        </div>
        <div style={p.statCard}>
          <span style={{ ...p.statValue, color: "#D4620D" }}>{fmtMoney(totalAmount)}</span>
          <span style={p.statLabel}>Total facturado</span>
        </div>
      </div>

      {/* Filters */}
      <div style={p.filtersRow}>
        <select
          style={p.filterSelect}
          value={filterUser}
          onChange={e => setFilterUser(e.target.value)}
        >
          <option value="">Todos los usuarios</option>
          {employees.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <select
          style={p.filterSelect}
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="paid">Pagado</option>
        </select>
        <button style={p.refreshBtn} onClick={load}>
          <RotateCcw size={14} />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={p.center}><Loader size={24} style={{ animation: "spin 1s linear infinite", color: "#D4620D" }} /></div>
      ) : error ? (
        <div style={p.center}><p style={{ color: "#ef4444" }}>{error}</p></div>
      ) : cycles.length === 0 ? (
        <div style={p.empty}>
          <FileText size={40} style={{ color: "var(--text-3)", marginBottom: 12 }} />
          <p style={p.emptyText}>No hay ciclos de facturación</p>
          {isAdminPlus && (
            <button style={p.newBtn} onClick={() => setModal({ mode: "create" })}>
              <Plus size={14} /> Crear primer ciclo
            </button>
          )}
        </div>
      ) : (
        <div style={p.list}>
          {cycles.map(cycle => (
            <CycleDetail
              key={cycle.id}
              cycle={cycle}
              isAdminPlus={isAdminPlus}
              onEdit={c => setModal({ mode: "edit", cycle: c })}
              onPay={c => setConfirm({ type: "pay", cycle: c })}
              onDelete={c => setConfirm({ type: "delete", cycle: c })}
            />
          ))}
        </div>
      )}

      {/* Global cycle modal */}
      {globalModal && (
        <GlobalCycleModal
          onClose={() => setGlobalModal(false)}
          onDone={load}
        />
      )}

      {/* Create/Edit modal */}
      {modal && (
        <CycleModal
          mode={modal.mode}
          cycle={modal.cycle}
          employees={employees}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {/* Confirm dialog */}
      {confirm && (
        <div style={m.overlay} onClick={e => e.target === e.currentTarget && setConfirm(null)}>
          <div style={cnf.box}>
            <p style={cnf.title}>
              {confirm.type === "pay" ? "Marcar como pagado" : "Eliminar ciclo"}
            </p>
            <p style={cnf.body}>
              {confirm.type === "pay"
                ? `¿Confirmar pago del ciclo de ${confirm.cycle.user_name} por ${fmtMoney(confirm.cycle.amount)}?`
                : `¿Eliminar el ciclo de ${confirm.cycle.user_name}? Esta acción no se puede deshacer.`}
            </p>
            <div style={cnf.btns}>
              <button style={cnf.cancel} onClick={() => setConfirm(null)} disabled={actionLoading}>
                Cancelar
              </button>
              <button
                style={confirm.type === "delete" ? cnf.danger : cnf.primary}
                disabled={actionLoading}
                onClick={() => confirm.type === "pay" ? handlePay(confirm.cycle) : handleDelete(confirm.cycle)}
              >
                {actionLoading
                  ? <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />
                  : confirm.type === "pay" ? "Confirmar pago" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const p = {
  root: { width: "100%" },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 800, color: "var(--text)", marginBottom: 4, letterSpacing: "-0.4px" },
  subtitle: { fontSize: 13, color: "var(--text-2)", margin: 0 },
  newBtn: {
    display: "flex", alignItems: "center", gap: 8,
    background: "#D4620D", color: "#fff", border: "none", borderRadius: 10,
    padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    boxShadow: "0 2px 8px rgba(212,98,13,0.3)",
  },
  globalBtn: {
    display: "flex", alignItems: "center", gap: 8,
    background: "var(--bg-card)", color: "#D4620D",
    border: "1.5px solid #D4620D", borderRadius: 10,
    padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  statsRow: { display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" },
  statCard: {
    flex: "1 1 140px", background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: 12, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 4,
  },
  statValue: { fontSize: 22, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.5px" },
  statLabel: { fontSize: 12, color: "var(--text-2)", fontWeight: 600 },
  filtersRow: { display: "flex", gap: 10, marginBottom: 20, alignItems: "center" },
  filterSelect: {
    flex: "0 0 220px", padding: "9px 12px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg-card)",
    color: "var(--text)", fontSize: 13, fontFamily: "Montserrat, sans-serif",
    cursor: "pointer",
  },
  refreshBtn: {
    width: 38, height: 38, borderRadius: 10, border: "1px solid var(--border)",
    background: "var(--bg-card)", color: "var(--text-2)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  center: { display: "flex", justifyContent: "center", alignItems: "center", paddingTop: 80 },
  empty: { display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 80, gap: 8 },
  emptyText: { fontSize: 15, color: "var(--text-2)", fontWeight: 600 },
  list: { display: "flex", flexDirection: "column", gap: 10 },
};

const d = {
  card: {
    background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: 14, overflow: "hidden",
  },
  cardHead: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "14px 16px", cursor: "default",
  },
  cycleIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: "var(--primary-bg)",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  cycleInfo: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
  cycleName: { fontSize: 14, fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cyclePeriod: { fontSize: 12, color: "var(--text-2)" },
  cycleRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 },
  amountText: { fontSize: 15, fontWeight: 800, letterSpacing: "-0.3px" },
  statusBadge: {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px",
    borderRadius: 100, padding: "3px 9px",
  },
  statusPaid:    { background: "#dcfce7", color: "#166534" },
  statusPending: { background: "#fef3c7", color: "#92400e" },
  expandBtn: {
    width: 32, height: 32, borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  cardBody: { borderTop: "1px solid var(--border)", padding: "16px 16px 14px" },
  metaRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
  metaLabel: { fontSize: 12, color: "var(--text-2)" },
  metaValue: { fontSize: 12, fontWeight: 700, color: "var(--text)" },
  metaDot: { width: 3, height: 3, borderRadius: "50%", background: "var(--text-3)" },
  notes: { fontSize: 13, color: "var(--text-2)", margin: "0 0 12px", lineHeight: 1.5, fontStyle: "italic" },
  fieldsSection: { marginBottom: 14 },
  fieldsTitle: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "var(--text-2)", marginBottom: 8 },
  fieldRow: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "8px 0", borderBottom: "1px solid var(--border)",
  },
  fieldName: { fontSize: 13, fontWeight: 700, color: "var(--text-2)", minWidth: 100 },
  fieldValue: { flex: 1, fontSize: 13, color: "var(--text)", fontFamily: "monospace" },
  copyBtn: {
    width: 28, height: 28, borderRadius: 7, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  routesSection: { marginBottom: 14 },
  routeItem: {
    display: "flex", flexDirection: "column", gap: 2,
    padding: "8px 0", borderBottom: "1px solid var(--border)",
  },
  routeItemName: { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  routeItemMeta: { fontSize: 12, color: "var(--text-2)" },
  routeChip: {
    fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 100,
    background: "var(--bg-subtle)", color: "var(--text-2)", border: "1px solid var(--border)",
  },
  premioChip: {
    fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 100,
    background: "#fff7ed", color: "#D4620D", border: "1px solid #fed7aa",
  },
  facturasSection: { marginBottom: 14 },
  facturasEmpty: { fontSize: 13, color: "var(--text-2)", margin: "4px 0 0", fontStyle: "italic" },
  facturaRow: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "8px 0", borderBottom: "1px solid var(--border)",
  },
  facturaIcon: {
    width: 30, height: 30, borderRadius: 8, background: "var(--primary-bg)",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  facturaInfo: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
  facturaName: { fontSize: 13, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  facturaMeta: { fontSize: 11, color: "var(--text-2)" },
  facturaThumbLink: { display: "block", flexShrink: 0 },
  facturaThumb: { width: 44, height: 44, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)", display: "block" },
  facturaOpenBtn: {
    display: "flex", alignItems: "center", gap: 5, flexShrink: 0,
    background: "var(--bg-subtle)", border: "1px solid var(--border)",
    borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700,
    color: "var(--text-2)", textDecoration: "none", cursor: "pointer",
  },
  aclaracionesBox: {
    background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
    padding: "12px 14px", marginBottom: 14,
  },
  aclaracionItem: { margin: 0, fontSize: 13, color: "#78350f", lineHeight: 1.6 },
  actions: { display: "flex", gap: 8, paddingTop: 8, flexWrap: "wrap" },
  payBtn: {
    display: "flex", alignItems: "center", gap: 6,
    background: "#dcfce7", color: "#166534", border: "none", borderRadius: 8,
    padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  editBtn: {
    display: "flex", alignItems: "center", gap: 6,
    background: "var(--bg-subtle)", color: "var(--text-2)", border: "1px solid var(--border)",
    borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  deleteBtn: {
    display: "flex", alignItems: "center", gap: 6,
    background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 8,
    padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
};

const m = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16,
  },
  sheet: {
    background: "var(--bg-card)", borderRadius: 18,
    width: "100%", maxWidth: 580, maxHeight: "90vh",
    display: "flex", flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 22px", borderBottom: "1px solid var(--border)",
  },
  headerTitle: { fontSize: 16, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.3px" },
  closeBtn: {
    width: 32, height: 32, borderRadius: 8,
    border: "1px solid var(--border)", background: "var(--bg-subtle)",
    color: "var(--text-2)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  body: { padding: "20px 22px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 12 },
  footer: {
    padding: "16px 22px", borderTop: "1px solid var(--border)",
    display: "flex", gap: 10, justifyContent: "flex-end",
  },
  label: { fontSize: 12, fontWeight: 700, color: "var(--text-2)", marginBottom: 4, display: "block" },
  input: {
    width: "100%", padding: "10px 12px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg-subtle)",
    color: "var(--text)", fontSize: 14, fontFamily: "Montserrat, sans-serif",
    boxSizing: "border-box", marginBottom: 0, outline: "none",
  },
  select: {
    width: "100%", padding: "10px 12px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg-subtle)",
    color: "var(--text)", fontSize: 14, fontFamily: "Montserrat, sans-serif",
    cursor: "pointer", boxSizing: "border-box",
  },
  sectionHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    borderTop: "1px solid var(--border)", paddingTop: 12,
  },
  sectionLabel: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "var(--text-2)" },
  addFieldBtn: {
    display: "flex", alignItems: "center", gap: 5,
    background: "none", border: "1px solid var(--border)", color: "#D4620D",
    borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  fieldRow: { display: "flex", gap: 8, alignItems: "center" },
  removeFieldBtn: {
    width: 30, height: 30, borderRadius: 8, border: "1px solid var(--border)",
    background: "var(--bg-subtle)", color: "var(--text-2)", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  routeCountBadge: {
    background: "var(--primary-bg)", color: "#D4620D",
    borderRadius: 100, fontSize: 11, fontWeight: 700, padding: "2px 9px",
  },
  routeEmpty: { fontSize: 13, color: "var(--text-2)", textAlign: "center", padding: "12px 0" },
  routeList: {
    maxHeight: 200, overflowY: "auto", border: "1px solid var(--border)",
    borderRadius: 10, padding: "4px 0",
  },
  routeRow: {
    display: "flex", gap: 10, padding: "10px 12px", alignItems: "flex-start",
    cursor: "pointer", borderBottom: "1px solid var(--border)",
  },
  routeRowDim: { opacity: 0.5 },
  routeInfo: { display: "flex", flexDirection: "column", gap: 2 },
  routeName: { fontSize: 13, fontWeight: 700, color: "var(--text)" },
  routeMeta: { fontSize: 12, color: "var(--text-2)" },
  inCycleTag: { color: "#f59e0b", fontWeight: 700 },
  errorText: { fontSize: 13, color: "#ef4444", fontWeight: 600, margin: 0 },
  cancelBtn: {
    padding: "10px 20px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg-subtle)",
    color: "var(--text-2)", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  saveBtn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 22px", borderRadius: 10, border: "none",
    background: "#D4620D", color: "#fff",
    fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
    boxShadow: "0 2px 8px rgba(212,98,13,0.3)",
  },
};

const cnf = {
  box: {
    background: "var(--bg-card)", borderRadius: 16, padding: "28px 28px 22px",
    width: "100%", maxWidth: 380,
    boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
  },
  title: { fontSize: 16, fontWeight: 800, color: "var(--text)", margin: "0 0 10px", letterSpacing: "-0.3px" },
  body:  { fontSize: 14, color: "var(--text-2)", margin: "0 0 22px", lineHeight: 1.55 },
  btns:  { display: "flex", gap: 10, justifyContent: "flex-end" },
  cancel: {
    padding: "10px 18px", borderRadius: 10,
    border: "1px solid var(--border)", background: "var(--bg-subtle)",
    color: "var(--text-2)", fontSize: 13, fontWeight: 700,
    cursor: "pointer", fontFamily: "Montserrat, sans-serif",
  },
  primary: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "10px 20px", borderRadius: 10, border: "none",
    background: "#D4620D", color: "#fff",
    fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
  danger: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "10px 20px", borderRadius: 10, border: "none",
    background: "#ef4444", color: "#fff",
    fontSize: 13, fontWeight: 700, cursor: "pointer",
    fontFamily: "Montserrat, sans-serif",
  },
};
